import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Button, Modal, Table, Dropdown } from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import AddSmig from "./AddSmig";



const { confirm } = Modal;

const Smigs = () => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${TOKEN}`,
      "Content-Type": "application/json",
    }),
    []
  );

  const fetchAll = () => {
    axios
      .get(`${API_URL}/api/smig`, { headers })
      .then((res) => {
        const sorted = [...res.data].sort((a, b) => {
          if (a.smigId != null && b.smigId != null) return b.smigId - a.smigId;
          return String(b.dateDebutApplication || "").localeCompare(
            String(a.dateDebutApplication || "")
          );
        });
        setData(sorted);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des SMIG.");
      });
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleShow = (isEdit = false, item = null) => {
    setEditMode(isEdit);
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedItem(null);
  };

  const handleAdd = (created) => {
    setData((prev) => {
      const arr = [created, ...prev];
      return arr.sort((a, b) => {
        if (a.smigId != null && b.smigId != null) return b.smigId - a.smigId;
        return String(b.dateDebutApplication || "").localeCompare(
          String(a.dateDebutApplication || "")
        );
      });
    });
  };

  const handleUpdate = (updated) => {
    setData((prev) =>
      prev
        .map((it) => (it.smigId === updated.smigId ? updated : it))
        .sort((a, b) => {
          if (a.smigId != null && b.smigId != null) return b.smigId - a.smigId;
          return String(b.dateDebutApplication || "").localeCompare(
            String(a.dateDebutApplication || "")
          );
        })
    );
  };

  const confirmDelete = (smigId) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer ce SMIG ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => doDelete(smigId),
    });
  };

  const doDelete = async (smigId) => {
    try {
      await axios.delete(`${API_URL}/api/smig/delete/${smigId}`, { headers });
      setData((prev) => prev.filter((it) => it.smigId !== smigId));
      toast.success("SMIG supprimé avec succès.");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const columns = [
    {
      title: "Pays",
      dataIndex: "pays",
      key: "pays",
      sorter: (a, b) =>
        String(a.pays || "").localeCompare(String(b.pays || "")),
    },
    {
      title: "Montant SMIG",
      dataIndex: "montantSmig",
      key: "montantSmig",
      align: "right",
      render: (v) =>
        v != null
          ? Number(v).toLocaleString("fr-FR", {
              style: "currency",
              currency: "XOF",
            })
          : "-",
      sorter: (a, b) => Number(a.montantSmig || 0) - Number(b.montantSmig || 0),
    },
    {
      title: "Début d'application",
      dataIndex: "dateDebutApplication",
      key: "dateDebutApplication",
      sorter: (a, b) =>
        String(a.dateDebutApplication || "").localeCompare(
          String(b.dateDebutApplication || "")
        ),
      render: (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "-"),
    },
    {
      title: "Fin d'application",
      dataIndex: "dateFinApplication",
      key: "dateFinApplication",
      sorter: (a, b) =>
        String(a.dateFinApplication || "").localeCompare(
          String(b.dateFinApplication || "")
        ),
      render: (v) => (v ? new Date(v).toLocaleDateString("fr-FR") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menuItems = [
          {
            key: "edit",
            label: "Modifier",
            onClick: () => handleShow(true, record),
          },
          {
            key: "delete",
            label: "Supprimer",
            danger: true,
            onClick: () => confirmDelete(record.smigId),
          },
        ];
        return (
          <Dropdown
            menu={{
              items: menuItems.map((it) => ({
                key: it.key,
                label: (
                  <span
                    style={it.danger ? { color: "#cf1322" } : {}}
                    onClick={(e) => {
                      e.preventDefault();
                      it.onClick();
                    }}
                  >
                    {it.label}
                  </span>
                ),
              })),
            }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleShow(false, null)}
                style={{ marginBottom: 16 }}
              >
                Ajouter
              </Button>

              <div className="card">
                <h5 className="card-header">Liste des SMIG</h5>
                <div className="table-responsive text-nowrap p-3">
                  <Table
                    rowKey={(r) => r.smigId}
                    dataSource={data}
                    columns={columns}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    size="middle"
                  />
                </div>
              </div>
            </div>

            <Footer />
          </div>
        </div>
      </div>

      <AddSmig
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedItem={selectedItem}
        items={data}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
      />
    </div>
  );
};

export default Smigs;
